import { createRouter, createWebHistory } from 'vue-router'

import { i18n, SUPPORTED_LOCALES, setLocale, loadLocale } from '@/i18n.js'

import store from '@/store/index.js'

import { onlineManager } from '@tanstack/vue-query'
import { Actions } from '@/store/Auth'

// With this we make the lang param can only contain those in langs
// Since `/:lang/` could also match `/dashboard/`
const langRegex = SUPPORTED_LOCALES.join('||')

function prefixRoutes(prefix, routes) {
  return routes.map((route) => ({
    ...route,
    path: `/${prefix}${route.path}`,
  }))
}

import routes from './routes.js'
import { useConfig } from '@/composables/useConfig'

const setupRouter = (config) => {
  const prefixedRoutes = prefixRoutes(`:lang(${langRegex})?`, routes)

  const router = createRouter({
    history: createWebHistory(config.FRONTEND_URL),
    routes: prefixedRoutes,
    scrollBehavior(to, from, savedPosition) {
      if (to.hash) {
        return {
          el: to.hash,
          behavior: 'smooth',
          top: 70,
        }
      }
      if (to.name === 'report' || to.name === from.name) {
        return
      }
      if (from.meta.restoreScroll && savedPosition) {
        return savedPosition
      }
      return { top: 0 }
    },
  })

  // LANGUAGE
  router.beforeEach(async (to) => {
    // Language if not provided
    if (!to.params.lang) {
      const defaultLang = localStorage.getItem('locale') || navigator.language.split('-')[0]
      const matchingLocale = SUPPORTED_LOCALES.includes(defaultLang)
        ? defaultLang
        : SUPPORTED_LOCALES.find((locale) => locale.startsWith(defaultLang))
      await setLocale(matchingLocale)
    }

    // Language provided and different than current
    if (to.params.lang && i18n.global.locale.value !== to.params.lang) {
      await setLocale(to.params.lang)
    }

    // Lazy loading language
    if (!i18n.global.availableLocales.includes(i18n.global.locale.value)) {
      await loadLocale(i18n.global.locale.value)
    }

    // TODO: can we inject lang at the router-link level, so it appears in link href?
    if (!to.params.lang && !to.meta.resetLangParam && i18n.global.locale.value) {
      return { ...to, params: { ...to.params, lang: i18n.global.locale.value } }
    }

    // TODO: refactor this / move to a route-specific beforeEnter guard
    // if (to.params.action === 'start' && to.redirectedFrom) {
    //     console.debug('Redirected from', to.redirectedFrom, to.params)
    //     // update localestorage lang on landing
    //     // TODO: why are we storing the short lang and not the full ISO code?
    //     const lang = to.redirectedFrom.???.split('/')[1]
    //     localStorage.setItem('lang', lang)
    // }
  })

  // // AUTHENTICATION
  router.beforeEach(async (to) => {
    // console.debug('Router Authentication beforeEach', to)

    const config = useConfig()

    if (config.CHECK_LOGIN && !store.state.auth.authenticated) {
      await store.dispatch(
        onlineManager.isOnline() ? Actions.CHECK_AUTHENTICATION : Actions.CHECK_OFFLINE_AUTHENTICATION
      )

      if (store.state.auth.authenticated) {
        // TODO: sometimes the /auth/enter call will return an invalid token error
        // and redirect here, but the user is still authenticated
        if (to.name === 'login') {
          console.debug('Authenticated, redirecting dashboard page', to.params)
          return {
            name: 'dashboard',
            params: { lang: to.params.lang },
          }
        } else return
      }

      if (to.name === 'login' || to.meta?.public) {
        return
      }
      if (config.UNAUTHORIZED_URL) {
        window.location.href = config.UNAUTHORIZED_URL
        return false
      }

      return {
        name: 'login',
        params: { lang: to.params.lang },
        query: { organization: to.query.organization, r: to.href },
      }
    }
  })

  // REQUEST REF
  router.beforeEach((to, from) => {
    if (to.query.valuation_request_ref !== undefined) {
      store.commit('SET_REQUEST_REF', to.query.valuation_request_ref)
    } else {
      // NOTE: reset of curRequest can be triggered:
      // 1. at route level, with route.meta.resetCurRequest
      // 2. at query level, with query.resetCurRequest
      // TODO: move toward splitting routes and handling everything through meta
      if (to.meta.resetCurRequest || to.query.resetCurRequest) {
        store.commit('RESET_REQUEST_REF')
        if (from.query.resetCurRequest && to.query.resetCurRequest)
          return { ...to, query: { ...to.query, resetCurRequest: undefined } }
      } else if (store.getters.getRequestRef) {
        return { ...to, query: { ...to.query, valuation_request_ref: store.getters.getRequestRef } }
      }
    }
  })

  // TODO: improve error handling to include full trace before re-enabling:
  // router.onError((err) => {
  //     const error = {
  //         error_code: 'router_error',
  //         debug: {
  //             message: `Vue Router error: ${err.message}`,
  //             traceback: err.stack,
  //         },
  //     }
  //     store.dispatch(Actions.LOG_ERROR, { error, errorType: 'error' })
  // })

  // Needed to avoid errors being thrown when doing a .push() without the language
  // (as the beforeEach will add it automatically, but result in a different target route)
  // https://github.com/vuejs/vue-router/issues/2881#issuecomment-520554378
  // const originalPush = Router.prototype.push
  // Router.prototype.push = function push(location, onResolve, onReject) {
  //     if (onResolve || onReject)
  //         return originalPush.call(this, location, onResolve, onReject)
  //     return originalPush.call(this, location).catch((err) => {
  //         if (Router.isNavigationFailure(err)) {
  //             // resolve err
  //             return err
  //         }
  //         // rethrow error
  //         return Promise.reject(err)
  //     })
  // }

  return router
}

export default setupRouter
